import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Team } from 'models/teams/Team';
import { ApiService } from 'services/ApiService';
import type { WorkflowInstanceSummaryDto } from 'models/workflows/WorkflowInstanceSummaryDto';
import type { WorkflowInstance } from 'models/workflows/WorkflowInstance';
import type { PagedResult } from 'models/PagedResult';
import type { ReviewDecisionSubmission } from 'models/submissions/ReviewDecisionSubmission';
import PaginationBar from 'components/shared/PaginationBar';
import SlideOver from 'components/shared/SlideOver';
import WorkflowInstanceView from 'components/workflows/WorkflowInstanceView';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OperationsHubProps = {
  team: Team;
  artistId: string;
  outlet?: React.ReactNode;
};

type SortColumn =
  | 'userFirstName'
  | 'templateName'
  | 'projectName'
  | 'status'
  | 'pendingEnrollmentType'
  | 'createdAt'
  | 'daysSinceLastInput';

type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'InProgress', label: 'In progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Failed', label: 'Failed' },
];

// ---------------------------------------------------------------------------
// Status badge helpers
// ---------------------------------------------------------------------------

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'InProgress':
      return 'bg-blue-100 text-blue-700 border border-blue-300';
    case 'Completed':
      return 'bg-green-100 text-green-700 border border-green-300';
    case 'Cancelled':
      return 'bg-gray-100 text-gray-600 border border-gray-300';
    case 'Failed':
      return 'bg-red-100 text-red-700 border border-red-300';
    default:
      return 'bg-gray-100 text-gray-600 border border-gray-300';
  }
};

const statusLabel = (status: string): string =>
  STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;

const pendingBadge = (type: string | null): React.ReactNode => {
  if (!type) return null;
  if (type === 'ReviewEnrollment')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
        Needs review
      </span>
    );
  if (type === 'FormEnrollment')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-300">
        Awaiting form
      </span>
    );
  if (type === 'SecurityDepositEnrollment')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-300">
        Awaiting deposit
      </span>
    );
  if (type === 'InvoiceEnrollment')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700 border border-teal-300">
        Invoice pending
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300">
      Pending
    </span>
  );
};

const daysBadge = (days: number, hours: number): React.ReactNode => {
  const urgent = days > 7;
  const label = days > 0 ? (hours > 0 ? `${days}d ${hours}h` : `${days}d`) : `${hours}h`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        urgent
          ? 'bg-red-100 text-red-700 border border-red-300'
          : 'bg-gray-100 text-gray-600 border border-gray-200'
      }`}
      title={urgent ? 'No activity for more than 7 days' : undefined}
    >
      {urgent && <span aria-hidden>!</span>}
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------

interface SortHeaderProps {
  label: string;
  column: SortColumn;
  current: SortColumn;
  dir: SortDir;
  onSort: (col: SortColumn) => void;
}

const SortHeader: React.FC<SortHeaderProps> = ({ label, column, current, dir, onSort }) => (
  <th
    scope="col"
    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 transition-colors"
    onClick={() => onSort(column)}
    aria-sort={
      current === column ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'
    }
  >
    <span className="flex items-center gap-1">
      {label}
      {current === column && (
        <span aria-hidden className="text-blue-500 text-base leading-none">
          {dir === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </span>
  </th>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const OperationsHub: React.FC<OperationsHubProps> = ({ team, artistId, outlet }) => {
  // --- Table state ---
  const [result, setResult] = useState<PagedResult<WorkflowInstanceSummaryDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Filter / sort state ---
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>('daysSinceLastInput');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [requiresReview, setRequiresReview] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Detail panel state ---
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailInstance, setDetailInstance] = useState<WorkflowInstance | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Project name inline-edit state ---
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [projectNameDraft, setProjectNameDraft] = useState('');
  const [projectNameSaving, setProjectNameSaving] = useState(false);

  // --- Archive state ---
  const [archiving, setArchiving] = useState(false);

  // ---------------------------------------------------------------------------
  // Fetch page
  // ---------------------------------------------------------------------------
  const fetchPage = useCallback(async () => {
    if (!team?.teamId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.getWorkflowInstances(team.teamId, {
        page,
        pageSize: PAGE_SIZE,
        sortBy: sortColumn,
        sortDir,
        requiresReview,
        includeArchived,
      });
      setResult(data);
    } catch {
      setError('Failed to load workflow instances. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [team?.teamId, page, sortColumn, sortDir, requiresReview, includeArchived]);

  useEffect(() => { void fetchPage(); }, [fetchPage]);

  // ---------------------------------------------------------------------------
  // Fetch detail
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedId) {
      setDetailInstance(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    ApiService.getWorkflowInstance(team.teamId, selectedId)
      .then((instance) => { if (!cancelled) setDetailInstance(instance); })
      .catch(() => { if (!cancelled) setDetailError('Failed to load instance details.'); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId, team.teamId]);

  // Reset inline-edit state whenever the selected row changes
  useEffect(() => {
    setEditingProjectName(false);
    setProjectNameDraft('');
  }, [selectedId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const resetPage = () => setPage(1);

  const handleSort = (col: SortColumn) => {
    if (col === sortColumn) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortDir('desc');
    }
    resetPage();
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(resetPage, 350);
  };

  const handleRequiresReviewChange = (checked: boolean) => {
    setRequiresReview(checked);
    resetPage();
  };

  const handleIncludeArchivedChange = (checked: boolean) => {
    setIncludeArchived(checked);
    resetPage();
  };

  const handleArchiveToggle = async (id: string, archive: boolean) => {
    setArchiving(true);
    try {
      await ApiService.setWorkflowInstanceArchived(team.teamId, id, archive);
      // If we just archived and we're not showing archived rows, close the panel
      if (archive && !includeArchived) {
        setSelectedId(null);
      }
      void fetchPage();
    } finally {
      setArchiving(false);
    }
  };

  const handleRowClick = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const handleSubmitReview = async (enrollmentId: string, submission: ReviewDecisionSubmission) => {
    setIsSubmitting(true);
    try {
      await ApiService.submitToEnrollment(enrollmentId, submission);
      if (selectedId) {
        const updated = await ApiService.getWorkflowInstance(team.teamId, selectedId);
        setDetailInstance(updated);
      }
      void fetchPage();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewComplete = async () => {
    if (selectedId) {
      const updated = await ApiService.getWorkflowInstance(team.teamId, selectedId);
      setDetailInstance(updated);
    }
    void fetchPage();
  };

  const handleSaveProjectName = async () => {
    if (!selectedId) return;
    const newName = projectNameDraft.trim() || null;
    setProjectNameSaving(true);
    try {
      await ApiService.updateWorkflowInstanceProjectName(team.teamId, selectedId, newName);
      setResult((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item.workflowInstanceId === selectedId
                  ? { ...item, projectName: newName }
                  : item
              ),
            }
          : prev
      );
      setEditingProjectName(false);
    } catch {
      // stay in edit mode on error so the user can retry
    } finally {
      setProjectNameSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Client-side search (name / template -- server handles sort/page/filter)
  // ---------------------------------------------------------------------------
  const rows = (result?.items ?? []).filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const fullName = `${row.userFirstName} ${row.userLastName}`.toLowerCase();
    return fullName.includes(term) || row.templateName.toLowerCase().includes(term);
  });

  const sortProps = { current: sortColumn, dir: sortDir, onSort: handleSort };

  const selectedRow = (result?.items ?? []).find((r) => r.workflowInstanceId === selectedId) ?? null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      {/* Page heading */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Operations Hub</h1>
        {team && <p className="text-sm text-gray-500 mt-0.5">{team.teamName}</p>}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3" role="search" aria-label="Filter workflow instances">
        {/* Search */}
        <label className="sr-only" htmlFor="ops-search">Search by name or workflow</label>
        <div className="relative">
          <input
            id="ops-search"
            type="search"
            placeholder="Search name or workflow..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-3 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
          />
        </div>

        {/* Needs review toggle */}
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={requiresReview}
            onChange={(e) => handleRequiresReviewChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Needs review only
        </label>

        {/* Include archived toggle */}
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => handleIncludeArchivedChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Include archived
        </label>

        {/* Refresh */}
        <button
          type="button"
          onClick={() => void fetchPage()}
          disabled={loading}
          aria-label="Refresh list"
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50"
        >
          <span aria-hidden className={loading ? 'animate-spin inline-block' : ''}>↻</span>
          Refresh
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between"
        >
          <span>{error}</span>
          <button type="button" onClick={() => void fetchPage()} className="underline ml-4 hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200" aria-label="Active workflow instances">
          <thead className="bg-gray-50">
            <tr>
              <SortHeader label="User" column="userFirstName" {...sortProps} />
              <SortHeader label="Workflow" column="templateName" {...sortProps} />
              <SortHeader label="Project name" column="projectName" {...sortProps} />
              <SortHeader label="Status" column="status" {...sortProps} />
              <SortHeader label="Pending action" column="pendingEnrollmentType" {...sortProps} />
              <SortHeader label="Idle" column="daysSinceLastInput" {...sortProps} />
              <SortHeader label="Started" column="createdAt" {...sortProps} />
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100" aria-live="polite" aria-busy={loading}>
            {/* Loading skeleton */}
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} aria-hidden>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-full max-w-[120px]" />
                    </td>
                  ))}
                </tr>
              ))}

            {/* Data rows */}
            {!loading &&
              rows.map((row) => {
                const isSelected = selectedId === row.workflowInstanceId;
                return (
                  <React.Fragment key={row.workflowInstanceId}>
                    <tr
                      onClick={() => handleRowClick(row.workflowInstanceId)}
                      className={`cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      aria-expanded={isSelected}
                      aria-haspopup="dialog"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(row.workflowInstanceId);
                        }
                      }}
                    >
                      {/* User */}
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                        <span className="flex items-center gap-2">
                          {row.userFirstName} {row.userLastName}
                          {row.isArchived && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                              Archived
                            </span>
                          )}
                        </span>
                      </td>

                      {/* Template */}
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate" title={row.templateName}>
                        {row.templateName}
                      </td>

                      {/* Project name */}
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate" title={row.projectName ?? undefined}>
                        {row.projectName ?? <span className="text-gray-400">&mdash;</span>}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(row.status)}`}>
                          {statusLabel(row.status)}
                        </span>
                      </td>

                      {/* Pending action */}
                      <td className="px-4 py-3">
                        {pendingBadge(row.pendingEnrollmentType)}
                      </td>

                      {/* Days idle */}
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {daysBadge(row.daysSinceLastInput, row.hoursSinceLastInput)}
                      </td>

                      {/* Started */}
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {new Date(row.createdAt).toLocaleDateString()}
                      </td>
                    </tr>

                  </React.Fragment>
                );
              })}

            {/* Empty state */}
            {!loading && rows.length === 0 && !error && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <p className="text-sm font-medium text-gray-700">No workflow instances found</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {requiresReview || searchTerm
                      ? 'Try adjusting your filters to see more results.'
                      : !includeArchived
                      ? 'Workflow instances will appear here once clients start a booking flow. Archived instances can be shown via “Include archived”.'
                      : 'Workflow instances will appear here once clients start a booking flow.'}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {result && result.totalPages > 1 && (
        <PaginationBar
          page={result.page}
          pageSize={result.pageSize}
          totalCount={result.totalCount}
          totalPages={result.totalPages}
          onPageChange={(p) => setPage(p)}
        />
      )}

      {/* Slide-over detail panel */}
      <SlideOver
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title={
          selectedRow ? (
            <span className="flex flex-col min-w-0 w-full">
              <span className="flex items-baseline gap-1.5 min-w-0">
                <span className="text-sm font-semibold text-gray-900 shrink-0">
                  {selectedRow.userFirstName} {selectedRow.userLastName}
                </span>
                <span className="text-gray-300 shrink-0" aria-hidden>·</span>
                {editingProjectName ? (
                  <input
                    autoFocus
                    type="text"
                    value={projectNameDraft}
                    onChange={(e) => setProjectNameDraft(e.target.value)}
                    onBlur={() => void handleSaveProjectName()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.currentTarget.blur(); }
                      if (e.key === 'Escape') {
                        setEditingProjectName(false);
                        setProjectNameDraft(selectedRow.projectName ?? '');
                      }
                    }}
                    disabled={projectNameSaving}
                    className="flex-1 min-w-0 text-sm font-semibold text-gray-900 border-b border-blue-400 bg-transparent focus:outline-none disabled:opacity-50"
                    aria-label="Edit project name"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setProjectNameDraft(selectedRow.projectName ?? '');
                      setEditingProjectName(true);
                    }}
                    className={`truncate text-sm font-semibold focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 rounded hover:underline ${
                      selectedRow.projectName ? 'text-gray-900' : 'text-gray-400 italic'
                    }`}
                    aria-label={selectedRow.projectName ? 'Edit project name' : 'Add project name'}
                  >
                    {selectedRow.projectName ?? 'Add project name'}
                  </button>
                )}
                {projectNameSaving && (
                  <span className="text-xs text-gray-400 ml-1 shrink-0">Saving…</span>
                )}
              </span>
              <span className="text-xs font-normal text-gray-500 mt-0.5 truncate">
                {selectedRow.templateName}
              </span>
            </span>
          ) : (
            'Workflow Details'
          )
        }
      >
        {detailLoading && (
          <div className="space-y-3 p-5" aria-label="Loading workflow details">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            ))}
          </div>
        )}
        {detailError && (
          <p role="alert" className="p-5 text-sm text-red-600">{detailError}</p>
        )}
        {!detailLoading && !detailError && detailInstance && (
          <>
            {/* Archive / Unarchive action */}
            {selectedRow && (
              <div className="px-5 pt-4 pb-0">
                <button
                  type="button"
                  onClick={() => void handleArchiveToggle(selectedRow.workflowInstanceId, !selectedRow.isArchived)}
                  disabled={archiving}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 ${
                    selectedRow.isArchived
                      ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                      : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'
                  }`}
                >
                  {archiving ? 'Saving…' : selectedRow.isArchived ? 'Unarchive' : 'Archive'}
                </button>
              </div>
            )}
            <WorkflowInstanceView
              workflowInstance={detailInstance}
              teamId={team.teamId}
              artistId={artistId}
              onSubmitReview={handleSubmitReview}
              isSubmitting={isSubmitting}
              onReviewComplete={() => { void handleReviewComplete(); }}
            />
          </>
        )}
      </SlideOver>

      {outlet}
    </div>
  );
};

export default OperationsHub;